import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent, type PointerEvent } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Check,
  ChevronRight,
  FolderOpen,
  Lightbulb,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Repeat,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { RowActions } from '@/components/ui/row-actions';
import type { Folder } from '../api/types';
import { RunningJobs } from './ActiveJobs';
import { useDiscoveryPaths } from '../workspace/paths';
import {
  FOLDER_GROUPS,
  FOLDER_SORT_LABELS,
  archivesInDays,
  archivesInLabel,
  filterFolders,
  folderGroupOf,
  groupFolders,
  openLeadCount,
  ownerNames,
  type FolderGroupId,
  type FolderSort,
} from '../workspace/folderModel';
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  clampWidth,
  readCollapsed,
  readCollapsedGroups,
  readOwner,
  readSort,
  readWidth,
  writeCollapsed,
  writeCollapsedGroups,
  writeOwner,
  writeSort,
  writeWidth,
} from '../workspace/sidebarPrefs';
import { NewFolderDialog, useFolderMenu } from '../workspace/FolderDialogs';
import { useArchiveAfterCompleteDays } from '../workspace/hooks';
import { FolderDot, MENU_CONTENT_CLS, MENU_ITEM_CLS } from '../workspace/ui';

interface Props {
  /** Passed in rather than fetched here — one folders query for the whole app. */
  folders: Folder[];
  selectedFolderId: string | null;
  outreachEnabled: boolean;
  currentUserId?: string | null;
}

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/**
 * Discovery's own rail: sections, then folders grouped Pinned · Active ·
 * Complete · Archived. Groups fold like the portal sidebar's (remembered, and
 * the group holding the open folder stays open). Width is draggable and the
 * rail collapses to icons; both are remembered.
 *
 * Uses card/canvas tones, not the `--sidebar-*` tokens: it renders beside the
 * portal's own nav, and two nav-styled columns would compete.
 */
export function Sidebar({ folders, selectedFolderId, outreachEnabled, currentUserId }: Props): JSX.Element {
  const paths = useDiscoveryPaths();
  const navigate = useNavigate();
  const [width, setWidth] = useState(readWidth);
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const [groupsCollapsed, setGroupsCollapsed] = useState(readCollapsedGroups);
  const [sort, setSortState] = useState<FolderSort>(readSort);
  const [owner, setOwnerState] = useState(readOwner);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const archiveAfterDays = useArchiveAfterCompleteDays();

  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    writeCollapsed(v);
  };
  const setSort = (s: FolderSort) => {
    setSortState(s);
    writeSort(s);
  };
  const setOwner = (o: string) => {
    setOwnerState(o);
    writeOwner(o);
  };
  const toggleGroup = (id: FolderGroupId) =>
    setGroupsCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsedGroups(next);
      return next;
    });

  const nav: NavEntry[] = [
    { to: paths.home, label: 'Folders', icon: FolderOpen, end: true },
    { to: paths.creators, label: 'Creators', icon: Users },
    { to: paths.repeatRuns(), label: 'Repeat runs', icon: Repeat },
    { to: paths.suggestions, label: 'Suggested searches', icon: Lightbulb },
    ...(outreachEnabled ? [{ to: paths.outreach, label: 'Outreach', icon: Send }] : []),
    { to: paths.settings, label: 'Settings', icon: Settings },
  ];

  const visible = useMemo(
    () => filterFolders(folders, { owner, currentUserId, search }),
    [folders, owner, currentUserId, search],
  );
  const groups = useMemo(() => groupFolders(visible, sort), [visible, sort]);
  const selectedGroup = useMemo(() => {
    const f = folders.find((x) => x.publicId === selectedFolderId);
    return f ? folderGroupOf(f) : null;
  }, [folders, selectedFolderId]);
  const owners = useMemo(() => ownerNames(folders), [folders]);

  // ── Resize ────────────────────────────────────────────────────────────────
  const drag = useRef<{ startX: number; startW: number } | null>(null);
  const onPointerDown = (ev: PointerEvent<HTMLDivElement>) => {
    drag.current = { startX: ev.clientX, startW: width };
    ev.currentTarget.setPointerCapture?.(ev.pointerId);
  };
  const onPointerMove = (ev: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setWidth(clampWidth(drag.current.startW + ev.clientX - drag.current.startX));
  };
  const endDrag = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    setWidth((w) => {
      writeWidth(w);
      return w;
    });
  }, []);
  const onHandleKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    const step = ev.shiftKey ? 32 : 8;
    if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
    ev.preventDefault();
    const next = clampWidth(width + (ev.key === 'ArrowRight' ? step : -step));
    setWidth(next);
    writeWidth(next);
  };

  if (collapsed) {
    return (
      <aside aria-label="Discovery" className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border bg-card py-3">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          className="mb-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <RunningJobs folders={folders} compact />
        <nav className="flex flex-col items-center gap-0.5" aria-label="Discovery sections">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={item.label}
              aria-label={item.label}
              className={({ isActive }) =>
                cn(
                  'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                  isActive ? 'bg-accent text-brand' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.75} />
            </NavLink>
          ))}
        </nav>
        <div className="my-2 h-px w-8 bg-border" />
        <div className="flex min-h-0 flex-1 flex-col items-center gap-0.5 overflow-y-auto">
          {[...groups.pinned, ...groups.active, ...groups.complete].map((f) => (
            <NavLink
              key={f.publicId}
              to={paths.folder(f.publicId)}
              title={f.name}
              aria-label={f.name}
              className={cn(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent',
                f.publicId === selectedFolderId && 'bg-accent',
              )}
            >
              <FolderDot folder={f} className="h-2.5 w-2.5" />
            </NavLink>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Discovery"
      className="relative flex shrink-0 flex-col border-r border-border bg-card"
      style={{ width }}
    >
      <div className="flex h-12 items-center gap-2 px-3">
        <span className="text-sm font-semibold">Discovery</span>
        <RunningJobs folders={folders} />
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>

      <nav className="space-y-0.5 px-2 pb-2" aria-label="Discovery sections">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors duration-[120ms]',
                isActive || (item.to === paths.home && !!selectedFolderId)
                  ? 'bg-accent font-semibold text-foreground shadow-[var(--highlight)]'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={cn('h-4 w-4 shrink-0', (isActive || (item.to === paths.home && !!selectedFolderId)) && 'text-brand')}
                  strokeWidth={1.85}
                />
                <span className="flex-1 truncate">{item.label}</span>
                {item.to === paths.home && folders.length > 0 && (
                  <span className="text-xs tabular-nums text-muted-foreground">{folders.length}</span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center gap-1.5 border-t border-border px-2 py-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" strokeWidth={2} />
          <Input
            className="h-8 pl-8 text-sm"
            placeholder="Filter folders"
            aria-label="Filter folders"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>
        <FolderViewMenu sort={sort} onSort={setSort} owner={owner} onOwner={setOwner} owners={owners} hasUser={!!currentUserId} />
        <button
          type="button"
          onClick={() => setCreating(true)}
          aria-label="New folder"
          title="New folder"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {visible.length === 0 && (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            {folders.length === 0 ? 'No folders yet. Create one with +.' : 'No folders match.'}
          </p>
        )}
        {FOLDER_GROUPS.map((g) => {
          const list = groups[g.id];
          if (list.length === 0) return null;
          const holdsSelected = selectedGroup === g.id;
          const isCollapsed = !holdsSelected && groupsCollapsed.has(g.id);
          const listId = `discovery-folder-group-${g.id}`;
          return (
            <div key={g.id} className="mt-2">
              <button
                type="button"
                onClick={() => toggleGroup(g.id)}
                aria-expanded={!isCollapsed}
                aria-controls={listId}
                disabled={holdsSelected}
                title={holdsSelected ? 'Holds the folder you have open' : undefined}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground"
              >
                <ChevronRight
                  aria-hidden
                  className={cn('h-3 w-3 shrink-0 transition-transform duration-150', !isCollapsed && 'rotate-90', holdsSelected && 'opacity-40')}
                  strokeWidth={2.25}
                />
                <span className="flex-1">{g.label}</span>
                <span className="tabular-nums">{list.length}</span>
              </button>
              <ul id={listId} hidden={isCollapsed} className="mt-0.5 space-y-px">
                {list.map((f) => (
                  <li key={f.publicId}>
                    <FolderRow
                      folder={f}
                      selected={f.publicId === selectedFolderId}
                      hint={g.id === 'complete' ? archivesInLabel(archivesInDays(f, archiveAfterDays)) : null}
                      onDeleted={() => {
                        if (f.publicId === selectedFolderId) navigate(paths.home);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onHandleKey}
        onDoubleClick={() => {
          setWidth(256);
          writeWidth(256);
        }}
        className="absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize outline-none transition-colors hover:bg-brand/20 focus-visible:bg-brand/30"
      />

      <NewFolderDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(folder) => navigate(paths.folder(folder.publicId))}
      />
    </aside>
  );
}

function FolderRow({
  folder,
  selected,
  hint,
  onDeleted,
}: {
  folder: Folder;
  selected: boolean;
  hint: string | null;
  onDeleted: () => void;
}): JSX.Element {
  const paths = useDiscoveryPaths();
  const menu = useFolderMenu(folder, { onDeleted });
  const open = openLeadCount(folder);
  const title = [folder.name, `${open.toLocaleString()} open lead${open === 1 ? '' : 's'}`, hint].filter(Boolean).join(' · ');

  return (
    <div
      className={cn(
        'group flex h-[38px] items-center gap-1 rounded-lg pr-0.5 transition-colors duration-[120ms]',
        selected ? 'bg-accent shadow-[var(--highlight)]' : 'hover:bg-accent/60',
      )}
    >
      <NavLink
        to={paths.folder(folder.publicId)}
        title={title}
        aria-current={selected ? 'page' : undefined}
        className="flex h-full min-w-0 flex-1 items-center gap-2.5 pl-2.5 text-sm"
      >
        <FolderDot folder={folder} />
        <span className={cn('min-w-0 flex-1 truncate', selected ? 'font-semibold text-foreground' : 'text-foreground/90')}>
          {folder.name}
        </span>
        {open > 0 && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{open.toLocaleString()}</span>}
      </NavLink>
      <RowActions
        triggerLabel={`Options for ${folder.name}`}
        items={[menu.items.rename, menu.items.purpose, menu.items.pin, menu.items.archive, menu.items.delete]}
      />
      {menu.dialogs}
    </div>
  );
}

function FolderViewMenu({
  sort,
  onSort,
  owner,
  onOwner,
  owners,
  hasUser,
}: {
  sort: FolderSort;
  onSort: (s: FolderSort) => void;
  owner: string;
  onOwner: (o: string) => void;
  owners: string[];
  hasUser: boolean;
}): JSX.Element {
  const active = sort !== 'recent' || owner !== '';
  const ownerChoices: { value: string; label: string }[] = [
    { value: '', label: 'Everyone' },
    ...(hasUser ? [{ value: 'mine', label: 'My folders' }] : []),
    ...owners.map((o) => ({ value: o, label: o })),
  ];
  // A remembered owner that no longer exists would hide every folder silently.
  useEffect(() => {
    if (owner && owner !== 'mine' && owners.length > 0 && !owners.includes(owner)) onOwner('');
  }, [owner, owners, onOwner]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Sort and filter folders"
          title="Sort and filter folders"
          className={cn(
            'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent hover:text-foreground',
            active ? 'text-brand' : 'text-muted-foreground',
          )}
        >
          <SlidersHorizontal className="h-4 w-4" strokeWidth={1.85} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className={cn(MENU_CONTENT_CLS, 'max-h-[70vh] min-w-[13rem] overflow-y-auto')}>
          <DropdownMenu.Label className="px-2 pb-1 pt-1.5 text-xs font-semibold text-muted-foreground">Sort by</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={sort} onValueChange={(v) => onSort(v as FolderSort)}>
            {(Object.keys(FOLDER_SORT_LABELS) as FolderSort[]).map((s) => (
              <DropdownMenu.RadioItem key={s} value={s} className={MENU_ITEM_CLS}>
                <span className="w-3.5">
                  <DropdownMenu.ItemIndicator>
                    <Check className="h-3.5 w-3.5" />
                  </DropdownMenu.ItemIndicator>
                </span>
                {FOLDER_SORT_LABELS[s]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2 pb-1 pt-1.5 text-xs font-semibold text-muted-foreground">Owner</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={owner} onValueChange={onOwner}>
            {ownerChoices.map((o) => (
              <DropdownMenu.RadioItem key={o.value || '__all__'} value={o.value} className={MENU_ITEM_CLS}>
                <span className="w-3.5">
                  <DropdownMenu.ItemIndicator>
                    <Check className="h-3.5 w-3.5" />
                  </DropdownMenu.ItemIndicator>
                </span>
                <span className="truncate">{o.label}</span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
